import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { trialDataEntryService } from '../../services/trialDataEntryService';
import { trialDataEntryApiService } from '../../services/trialDataEntryApiService';
import { trialService } from '../../services/trialService';
import { trialCentresService } from '../../services/trialCentresService';
import { locationManagementService } from '../../services/locationManagementService';
import {
  FileText,
  MapPin,
  Calendar,
  Plus,
  Search,
  Filter,
  Eye,
  Save,
  Users,
  Sprout,
  Eye as EyeIcon,
  BarChart3,
  Clock,
  CheckCircle,
  AlertCircle,
  Play,
  List,
  Grid,
  Camera,
  Upload,
  X,
  User,
  Home,
  Edit
} from 'lucide-react';

const TrialDataEntry = () => {
  const { user } = useAuth();
  const [selectedTrial, setSelectedTrial] = useState('');
  const [selectedCentre, setSelectedCentre] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  const [showDataEntryForm, setShowDataEntryForm] = useState(false);
  const [showDataEntryModal, setShowDataEntryModal] = useState(false);
  const [showLocationDetailsModal, setShowLocationDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [entriesByLocation, setEntriesByLocation] = useState({});
  const [savedEntries, setSavedEntries] = useState([]);
  const [currentEntry, setCurrentEntry] = useState({
    observation: '',
    replication: '',
    culture: '',
    value: ''
  });
  const [farmerFeedback, setFarmerFeedback] = useState('');
  const [locationInchargeFeedback, setLocationInchargeFeedback] = useState('');
  const [editingEntryId, setEditingEntryId] = useState(null);

  const { register, handleSubmit, reset, formState: { errors }, setValue, getValues } = useForm();

  const [allTrialsData, setAllTrialsData] = useState([]);

  // Load all trials and assignments from API (no localStorage fallback)
  const loadAllTrialsData = async () => {
    try {
      setIsLoading(true);
      const [trialsResponse, assignmentsResponse] = await Promise.all([
        trialService.getTrials({ page: 1, limit: 1000 }),
        trialDataEntryService.getTrialAssignments()
      ]);

      if (trialsResponse.success && assignmentsResponse.success) {
        const trials = trialsResponse.data || trialsResponse;
        const assigns = assignmentsResponse.data || assignmentsResponse;
        
        // Process trials directly since assignments don't contain location data
        const processedTrials = trials.map(trial => ({
          id: trial.id,
          trialCode: trial.code || '-',
          trialName: trial.name || '-',
          crop: trial.crop_name || '-',
          season: trial.season_name || '-',
          year: trial.year || '-',
          status: trial.status || 'active',
          locations: [] // No location data from assignments
        }));
        setAllTrialsData(processedTrials);
      } else {
        throw new Error('Failed to load trials or assignments');
      }
    } catch (error) {
      console.error('Failed to load trials data:', error);
      toast.error('Failed to load trials data');
      setAllTrialsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const [uniqueTrials, setUniqueTrials] = useState([]);
  const [centresForTrial, setCentresForTrial] = useState([]);
  const [locationsForCentre, setLocationsForCentre] = useState([]);
  const [filteredTrials, setFilteredTrials] = useState([]);
  const [isLoadingCentres, setIsLoadingCentres] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  
  // State for loaded data after Apply button
  const [loadedTrialData, setLoadedTrialData] = useState(null);
  const [loadedCentreData, setLoadedCentreData] = useState(null);
  const [loadedLocationData, setLoadedLocationData] = useState(null);
  const [isLoadingLocationDetails, setIsLoadingLocationDetails] = useState(false);
  
  // State for assignment data
  const [trialAssignments, setTrialAssignments] = useState([]);
  const [centreAssignments, setCentreAssignments] = useState([]);
  const [locationAssignments, setLocationAssignments] = useState([]);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

  // Helper function to get centre name from code
  const getCentreName = (centreCode) => {
    // If it's a full location code, extract the centre part
    const code = centreCode.length > 20 ? centreCode.slice(0, -3) : centreCode;
    
    // Map common centre codes to readable names
    const centreNameMap = {
      'ARTRICE2024KHARIFA': 'Coimbatore Research Station',
      'ARTRICE2024RABI': 'Madurai Research Station', 
      'ARTRICE2024SUMMER': 'Thanjavur Research Station',
      // Add more mappings as needed
    };
    return centreNameMap[code] || code;
  };

  // Update derived data when allTrialsData changes
  useEffect(() => {
    // Update unique trials - store objects with id and name
    const uniqueTrialNames = [...new Set(allTrialsData.map(trial => trial.trialName))];
    const trials = uniqueTrialNames.map(trialName => {
      const trial = allTrialsData.find(t => t.trialName === trialName);
      return {
        id: trial.id,
        name: trial.trialName
      };
    });
    setUniqueTrials(trials.sort((a, b) => a.name.localeCompare(b.name)));

    // Update filtered trials
    const filtered = allTrialsData.filter(trial => {
      const matchesSearch = trial.trialName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           trial.trialCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           trial.crop.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || trial.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
    setFilteredTrials(filtered);
  }, [allTrialsData, searchTerm, filterStatus]);

  // Load centres when selected trial changes
  const loadCentresForTrial = async (trialId) => {
    try {
      setIsLoadingCentres(true);
      const response = await trialCentresService.getTrialCentres({
        page: 1,
        limit: 100,
        trial_id: trialId
      });
      
      if (response.success) {
        const centres = response.data.map(centre => ({
          id: centre.id,
          code: centre.code || centre.centre_code || '-',
          name: centre.name || centre.centre_name || '-',
          address: centre.address || '-',
          status: centre.status || 'active'
        }));
        setCentresForTrial(centres);
      } else {
        console.error('Failed to load centres:', response.message);
        setCentresForTrial([]);
      }
    } catch (error) {
      console.error('Error loading centres:', error);
      toast.error('Failed to load centres for selected trial');
      setCentresForTrial([]);
    } finally {
      setIsLoadingCentres(false);
    }
  };

  // Update centres when selected trial changes
  useEffect(() => {
    if (!selectedTrial) {
      setCentresForTrial([]);
      return;
    }
    
    loadCentresForTrial(selectedTrial);
  }, [selectedTrial]);

  // Load locations when selected centre changes
  const loadLocationsForCentre = async (trialCentreId) => {
    try {
      setIsLoadingLocations(true);
      const response = await locationManagementService.getLocationManagement(trialCentreId, {
        page: 1,
        limit: 100
      });
      
      if (response.success) {
        const locations = response.data.map(location => ({
          id: location.id,
          locationName: location.location_name || '-',
          locationCode: location.sf_number || '-',
          culture: '-',
          treatment: '-',
          replications: 1,
          observations: [],
          lastEntryDate: '-',
          startDate: location.from_date || '-',
          status: location.status || 'active',
          pendingEntries: 0,
          assignedOfficer: location.location_incharge || '-',
          address: location.address || '-',
          latitude: location.latitude || null,
          longitude: location.longitude || null
        }));
        setLocationsForCentre(locations);
      } else {
        console.error('Failed to load locations:', response.message);
        setLocationsForCentre([]);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      toast.error('Failed to load locations for selected centre');
      setLocationsForCentre([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Load trial data by ID
  const loadTrialById = async (trialId) => {
    try {
      const response = await trialService.getTrial(trialId);
      if (response.success) {
        const trial = response.data;
        setLoadedTrialData({
          id: trial.id,
          trialCode: trial.code || '-',
          trialName: trial.title || trial.name || '-',
          description: trial.description || '-',
          trialType: trial.trial_type || '-',
          year: trial.year || '-',
          trialIncharge: trial.trial_incharge || '-',
          trialInchargeOfficer: trial.trial_incharge_officer || '-',
          replications: trial.replications || '-',
          observations: trial.observations || [],
          completionDate: trial.completion_date || '-',
          status: trial.status || 'active',
          startDate: trial.start_date || '-',
          endDate: trial.end_date || '-',
          centreId: trial.centre_id || '-',
          cropId: trial.crop_id || '-',
          soilTypeId: trial.soil_type_id || '-',
          seasonId: trial.season_id || '-',
          cropName: trial.crop_name || '-',
          cropVariety: trial.crop_variety || '-',
          seasonName: trial.season_name || '-',
          trials: trial.trials || []
        });
      }
    } catch (error) {
      console.error('Error loading trial data:', error);
      toast.error('Failed to load trial data');
    }
  };

  // Load centre data by ID
  const loadCentreById = async (centreId) => {
    try {
      const response = await trialCentresService.getTrialCentre(centreId);
      if (response.success) {
        const centre = response.data;
        setLoadedCentreData({
          id: centre.id,
          code: centre.code || centre.centre_code || '-',
          name: centre.name || centre.centre_name || '-',
          address: centre.address || '-',
          status: centre.status || 'active'
        });
      }
    } catch (error) {
      console.error('Error loading centre data:', error);
      toast.error('Failed to load centre data');
    }
  };

  // Load location data by ID
  const loadLocationById = async (locationId) => {
    try {
      const response = await locationManagementService.getLocationManagementById(locationId);
      if (response.success) {
        const location = response.data;
        setLoadedLocationData({
          id: location.id,
          locationName: location.location_name || '-',
          locationCode: location.sf_number || '-',
          culture: '-',
          treatment: '-',
          replications: 1,
          observations: [],
          lastEntryDate: '-',
          startDate: location.from_date || '-',
          status: location.status || 'active',
          pendingEntries: 0,
          assignedOfficer: location.location_incharge || '-',
          address: location.address || '-',
          latitude: location.latitude || null,
          longitude: location.longitude || null
        });
      }
    } catch (error) {
      console.error('Error loading location data:', error);
      toast.error('Failed to load location data');
    }
  };

  // Load trial assignments
  const loadTrialAssignments = async (trialId) => {
    try {
      const response = await trialService.getTrialAssignments(trialId);
      if (response.success) {
        setTrialAssignments(response.data || []);
      } else {
        setTrialAssignments([]);
      }
    } catch (error) {
      console.error('Error loading trial assignments:', error);
      setTrialAssignments([]);
    }
  };

  // Load centre assignments
  const loadCentreAssignments = async (trialCentreId) => {
    try {
      const response = await trialCentresService.getTrialCentreAssignments(trialCentreId);
      if (response.success) {
        setCentreAssignments(response.data || []);
      } else {
        setCentreAssignments([]);
      }
    } catch (error) {
      console.error('Error loading centre assignments:', error);
      setCentreAssignments([]);
    }
  };

  // Load location assignments
  const loadLocationAssignments = async (locationId) => {
    try {
      const response = await locationManagementService.getLocationAssignments(locationId);
      if (response.success) {
        setLocationAssignments(response.data || []);
      } else {
        setLocationAssignments([]);
      }
    } catch (error) {
      console.error('Error loading location assignments:', error);
      setLocationAssignments([]);
    }
  };

  // Load trial data entries from API
  const loadTrialDataEntries = async (locationId = null) => {
    try {
      setIsLoading(true);
      const params = {
        page: 1,
        limit: 100
      };
      
      // Add location_id if provided
      if (locationId) {
        params.location_id = locationId;
      }
      
      const response = await trialDataEntryApiService.getTrialDataEntries(params);
      
      if (response.success) {
        const entries = response.data.map(entry => {
          // Parse image JSON string
          let images = [];
          try {
            if (entry.image) {
              images = JSON.parse(entry.image);
            }
          } catch (error) {
            console.warn('Failed to parse image JSON:', error);
            images = [];
          }

          return {
            id: entry.id,
            observation: entry.observation,
            replication: entry.replication,
            culture: entry.culture,
            value: entry.value,
            entryDate: entry.entry_date,
            status: entry.status,
            timestamp: entry.created_at,
            officerId: entry.officer_id,
            officerName: entry.officer_name,
            officerEmail: entry.officer_email,
            createdAt: entry.created_at,
            updatedAt: entry.updated_at,
            locationId: entry.location_id,
            locationName: entry.location_name,
            sfNumber: entry.sf_number,
            farmerFeedback: entry.farmer_feedback || '',
            inchargeFeedback: entry.incharge_feedback || '',
            images: images // Array of image objects with IDs
          };
        });
        setSavedEntries(entries);
      } else {
        console.error('Failed to load trial data entries:', response.message);
        setSavedEntries([]);
      }
    } catch (error) {
      console.error('Error loading trial data entries:', error);
      toast.error('Failed to load trial data entries');
      setSavedEntries([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Update locations when selected centre changes
  useEffect(() => {
    if (!selectedTrial || !selectedCentre) {
      setLocationsForCentre([]);
      return;
    }
    
    // Find the selected centre
    const selectedCentreData = centresForTrial.find(c => c.id == selectedCentre);
    if (!selectedCentreData) {
      setLocationsForCentre([]);
      return;
    }
    
    // Load locations using the trial_centre_id from the selected centre
    loadLocationsForCentre(selectedCentre);
  }, [selectedTrial, selectedCentre, centresForTrial]);

  // Load data on component mount
  useEffect(() => {
    loadAllTrialsData();
  }, []);

  // Load data entries for selected location
  const loadDataEntries = async (trialId, locationId) => {
    try {
      const response = await trialDataEntryService.getDataEntries(trialId, locationId);
      if (response.success) {
        setSavedEntries(response.data || response);
      } else {
        throw new Error(response.message || 'Failed to load data entries');
      }
    } catch (error) {
      console.warn('API failed to load data entries:', error.message);
      // Keep existing entries as fallback
    }
  };

  // Function to download and display images from server
  const downloadAndDisplayImage = async (imageId) => {
    try {
      const blob = await trialDataEntryApiService.downloadDocument(imageId);
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to download image:', error);
      return null;
    }
  };

  // Photo upload functions
  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    try {
      setIsLoading(true);
      
      // Upload photos to API using the new endpoint
      const uploadPromises = files.map(async (file) => {
        const response = await trialDataEntryApiService.uploadImage(file);
        
        if (response.success) {
          return {
            id: response.data?.id || Date.now() + Math.random(),
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            preview: URL.createObjectURL(file),
            uploaded: true,
            serverId: response.data?.id // Store server ID for future reference
          };
        } else {
          throw new Error(response.message || 'Failed to upload photo');
        }
      });
      
      const newPhotos = await Promise.all(uploadPromises);
      setUploadedPhotos(prev => [...prev, ...newPhotos]);
      toast.success(`${files.length} photo(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error('Failed to upload photos');
      
      // Fallback to local preview only
      const newPhotos = files.map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: URL.createObjectURL(file),
        uploaded: false
      }));
      setUploadedPhotos(prev => [...prev, ...newPhotos]);
    } finally {
      setIsLoading(false);
    }
  };

  const removePhoto = (photoId) => {
    setUploadedPhotos(prev => {
      const photo = prev.find(p => p.id === photoId);
      if (photo?.preview) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter(p => p.id !== photoId);
    });
  };

  // Filter functions
  const handleApplyFilters = async () => {
    if (!selectedTrial || !selectedCentre || !selectedLocation) {
      toast.error('Please select Trial, Centre, and Location');
      return;
    }
    
    setIsLoadingLocationDetails(true);
    setShowLocationDetails(true);
    setShowDataEntryForm(true);
    
    try {
      // Load fresh data from API using the selected IDs
      await Promise.all([
        loadTrialById(selectedTrial),
        loadCentreById(selectedCentre),
        loadLocationById(selectedLocation)
      ]);
      
      // Load assignment data
      setIsLoadingAssignments(true);
      await Promise.all([
        loadTrialAssignments(selectedTrial),
        loadCentreAssignments(selectedCentre),
        loadLocationAssignments(selectedLocation)
      ]);
      
      // Load data entries for the selected location
      await loadDataEntries(selectedTrial, selectedLocation);
      
      // Load trial data entries from API with location_id
      await loadTrialDataEntries(selectedLocation);
    } catch (error) {
      console.error('Error loading location details:', error);
      toast.error('Failed to load location details');
    } finally {
      setIsLoadingLocationDetails(false);
      setIsLoadingAssignments(false);
    }
  };

  const handleResetFilters = () => {
    setSelectedTrial('');
    setSelectedCentre('');
    setSelectedLocation('');
    setShowLocationDetails(false);
    setShowDataEntryForm(false);
  };



  const openDataEntryModal = (trial, location) => {
    setSelectedTrial(trial.id);
    setSelectedLocation(location);
    setShowDataEntryModal(true);
    reset();
  };

  const openLocationDetailsModal = (location) => {
    setSelectedLocation(location);
    setShowLocationDetailsModal(true);
  };

  const handleCurrentEntryChange = (field, value) => {
    setCurrentEntry(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEntry = async () => {
    if (!currentEntry.observation || !currentEntry.replication || !currentEntry.culture || !currentEntry.value) {
      toast.error('Please fill all fields: Observation, Replication, Culture, and Value');
      return;
    }

    if (isNaN(currentEntry.value) || currentEntry.value <= 0) {
      toast.error('Please enter a valid positive number for the value');
      return;
    }

    try {
      setIsLoading(true);

      // Prepare images data for JSON stringify (only IDs)
      const imagesData = uploadedPhotos.map(photo => ({
        id: photo.serverId || photo.id
      }));

      // Prepare payload in the required format
      const payload = {
        officer_id: user?.id || 5, // Use current user ID or default to 5
        entry_date: new Date().toISOString().split('T')[0], // Format: YYYY-MM-DD
        observation: currentEntry.observation,
        replication: currentEntry.replication,
        culture: currentEntry.culture,
        value: parseFloat(currentEntry.value),
        status: 'active',
        location_id: selectedLocation, // Add location_id to the payload
        image: JSON.stringify(imagesData), // Add images as JSON string
        farmer_feedback: farmerFeedback || '', // Add farmer feedback
        incharge_feedback: locationInchargeFeedback || '' // Add location incharge feedback
      };

      if (editingEntryId) {
        // Update existing entry via API
        const response = await trialDataEntryApiService.updateTrialDataEntry(editingEntryId, payload);

        if (response.success) {
          setSavedEntries(prev => prev.map(entry => 
            entry.id === editingEntryId 
              ? {
                  ...entry,
                  observation: currentEntry.observation,
                  replication: currentEntry.replication,
                  culture: currentEntry.culture,
                  value: parseFloat(currentEntry.value),
                  entryDate: payload.entry_date,
                  timestamp: new Date().toISOString()
                }
              : entry
          ));
          setEditingEntryId(null);
          toast.success('Entry updated successfully!');
          
          // Clear all fields after successful update
          setCurrentEntry({
            observation: '',
            replication: '',
            culture: '',
            value: ''
          });
          setUploadedPhotos([]);
          setFarmerFeedback('');
          setLocationInchargeFeedback('');
        } else {
          throw new Error(response.message || 'Failed to update entry');
        }
      } else {
        // Create new entry via API
        const response = await trialDataEntryApiService.createTrialDataEntry(payload);

        if (response.success) {
          const newEntry = {
            id: response.data?.id || Date.now(),
            observation: currentEntry.observation,
            replication: currentEntry.replication,
            culture: currentEntry.culture,
            value: parseFloat(currentEntry.value),
            entryDate: payload.entry_date,
            timestamp: new Date().toISOString()
          };

          setSavedEntries(prev => [...prev, newEntry]);
          toast.success('Entry saved successfully!');
          
          // Clear all fields after successful save
          setCurrentEntry({
            observation: '',
            replication: '',
            culture: '',
            value: ''
          });
          setUploadedPhotos([]);
          setFarmerFeedback('');
          setLocationInchargeFeedback('');
        } else {
          throw new Error(response.message || 'Failed to save entry');
        }
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error(error.message || 'Failed to save entry');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditEntry = async (entry) => {
    try {
      // Set form fields
      setCurrentEntry({
        observation: entry.observation,
        replication: entry.replication,
        culture: entry.culture,
        value: entry.value.toString()
      });

      // Set feedback fields
      setFarmerFeedback(entry.farmerFeedback || '');
      setLocationInchargeFeedback(entry.inchargeFeedback || '');

      // Download and set images
      if (entry.images && entry.images.length > 0) {
        const imagePromises = entry.images.map(async (imageObj) => {
          const imageUrl = await downloadAndDisplayImage(imageObj.id);
          if (imageUrl) {
            return {
              id: imageObj.id,
              serverId: imageObj.id,
              name: `Image ${imageObj.id}`,
              size: 0, // Unknown size from server
              type: 'image/*',
              preview: imageUrl,
              uploaded: true
            };
          }
          return null;
        });

        const downloadedImages = await Promise.all(imagePromises);
        const validImages = downloadedImages.filter(img => img !== null);
        setUploadedPhotos(validImages);
      } else {
        setUploadedPhotos([]);
      }

      setEditingEntryId(entry.id);
      toast.success('Entry loaded for editing');
    } catch (error) {
      console.error('Error loading entry for editing:', error);
      toast.error('Failed to load entry for editing');
    }
  };

  const handleCancelEdit = () => {
    // Clear all form fields
    setCurrentEntry({
      observation: '',
      replication: '',
      culture: '',
      value: ''
    });
    
    // Clear images and feedback
    setUploadedPhotos([]);
    setFarmerFeedback('');
    setLocationInchargeFeedback('');
    
    // Reset edit mode
    setEditingEntryId(null);
    toast.success('Edit cancelled');
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      setIsLoading(true);
      const response = await trialDataEntryApiService.deleteTrialDataEntry(entryId);
      
      if (response.success) {
    setSavedEntries(prev => prev.filter(entry => entry.id !== entryId));
    toast.success('Entry deleted successfully!');
      } else {
        throw new Error(response.message || 'Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(error.message || 'Failed to delete entry');
    } finally {
      setIsLoading(false);
    }
  };

  // Export data entries
  const handleExportDataEntries = async (format = 'excel') => {
    if (!selectedTrial || !selectedLocation) {
      toast.error('Please select a trial and location first');
      return;
    }

    try {
      setIsLoading(true);
      const response = await trialDataEntryService.exportDataEntries(
        selectedTrial,
        selectedLocation.id,
        format
      );
      
      // Create download link for the exported file
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trial-${selectedTrial}-location-${selectedLocation.id}-entries.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Data entries exported successfully!');
    } catch (error) {
      console.error('Error exporting data entries:', error);
      toast.error('Failed to export data entries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataEntrySubmit = async (data) => {
    setIsLoading(true);
    try {
      // Prepare data entry payload
      const entryData = {
        trialId: selectedTrial,
        locationId: selectedLocation.id,
        observation: data.observation,
        replication: data.replication,
        culture: data.culture,
        value: data.value,
              photos: uploadedPhotos,
        farmerFeedback: farmerFeedback,
        locationInchargeFeedback: locationInchargeFeedback,
              timestamp: new Date().toISOString()
      };

      // Submit to API
      const response = await trialDataEntryService.submitDataEntry(
        selectedTrial,
        selectedLocation.id,
        entryData
      );

      if (response.success) {
        toast.success('Data entry saved successfully!');
        setShowDataEntryModal(false);
        setUploadedPhotos([]);
        setFarmerFeedback('');
        setLocationInchargeFeedback('');
        reset();
        
        // Reload data to reflect changes
        await loadAllTrialsData();
      } else {
        throw new Error(response.message || 'Failed to save data entry');
      }
    } catch (error) {
      console.error('Error saving data entry:', error);
      toast.error(error.message || 'Failed to save data entry');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <FileText className="w-8 h-8 mr-3 text-primary-600" />
            Trial Data Entry
          </h1>
          <p className="text-gray-600 mt-2">
            Monitor and manage trial data entries across all locations and officers
          </p>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Trial Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Trial Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Trial Name *</label>
              <select
                value={selectedTrial}
                onChange={(e) => {
                  setSelectedTrial(e.target.value);
                  setSelectedCentre('');
                  setSelectedLocation('');
                  setShowLocationDetails(false);
                  setShowDataEntryForm(false);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Trial</option>
                {uniqueTrials.map((trial) => (
                  <option key={trial.id} value={trial.id}>
                    {trial.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Centre Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Centre Name *</label>
              <select
                value={selectedCentre}
                onChange={(e) => {
                  setSelectedCentre(e.target.value);
                  setSelectedLocation('');
                  setShowLocationDetails(false);
                  setShowDataEntryForm(false);
                }}
                disabled={!selectedTrial || isLoadingCentres}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">
                  {isLoadingCentres ? 'Loading centres...' : 'Select Centre'}
                </option>
                {centresForTrial.map((centre) => (
                  <option key={centre.id} value={centre.id}>
                    {centre.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location Name *</label>
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setShowLocationDetails(false);
                  setShowDataEntryForm(false);
                }}
                disabled={!selectedCentre || isLoadingLocations}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">
                  {isLoadingLocations ? 'Loading locations...' : 'Select Location'}
                </option>
                {locationsForCentre.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.locationName}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-center gap-4">
            <button
              onClick={handleApplyFilters}
              disabled={!selectedTrial || !selectedCentre || !selectedLocation}
              className="btn-primary px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
            <button
              onClick={handleResetFilters}
              className="btn-outline px-6 py-3 text-lg"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Location Details - Only shown after applying filters */}
        {showLocationDetails && selectedLocation && (
          <div className="space-y-6">
            {isLoadingLocationDetails && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-gray-600">Loading location details...</span>
              </div>
            )}
            {!isLoadingLocationDetails && (
              <>
                {/* Location Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <MapPin className="w-5 h-5 mr-2 text-primary-600" />
                      Location Details
                    </h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Home className="w-4 h-4 mr-1" />
                        Trial: {loadedTrialData?.trialName || '-'} ({loadedTrialData?.trialCode || '-'})
                      </span>
                      <span className="flex items-center">
                        <Home className="w-4 h-4 mr-1" />
                        Centre: {loadedCentreData?.name || '-'}
                      </span>
                      <span className="flex items-center">
                        <Home className="w-4 h-4 mr-1" />
                        Location: {loadedLocationData?.locationName || '-'}
                      </span>
                      <span className="flex items-center">
                        <Home className="w-4 h-4 mr-1" />
                        Type: {loadedTrialData?.trialType || '-'}
                      </span>
                      <span className="flex items-center">
                        <Home className="w-4 h-4 mr-1" />
                        Year: {loadedTrialData?.year || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Trial Description */}
              {loadedTrialData?.description && loadedTrialData.description !== '-' && (
                <div className="px-6 py-4 bg-blue-50 border-b border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Trial Description</h4>
                  <p className="text-sm text-gray-700">{loadedTrialData.description}</p>
                </div>
              )}

              {/* Location Information */}
              <div className="px-6 py-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Location Information</h4>
                <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Observation:</span>
                      <span className="font-medium">
                        {loadedTrialData?.observations?.map(obs => `${obs.observation} (${obs.days} days)`).join(', ') || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Replication:</span>
                      <span className="font-medium">
                        {loadedTrialData?.replications || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Culture:</span>
                      <span className="font-medium">
                        {loadedTrialData?.trials?.map(t => t.culture).join(', ') || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Trial Type:</span>
                      <span className="font-medium">
                        {loadedTrialData?.trialType || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Year:</span>
                      <span className="font-medium">
                        {loadedTrialData?.year || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Crop:</span>
                      <span className="font-medium">
                        {loadedTrialData?.cropName || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Season:</span>
                      <span className="font-medium">
                        {loadedTrialData?.seasonName || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Trial Incharge:</span>
                      <span className="font-medium">
                        {loadedTrialData?.trialIncharge || '-'}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        loadedTrialData?.status || 'active'
                      )}`}>
                        {loadedTrialData?.status || 'active'}
                      </span>
                    </div>
                  </div>
              </div>

              {/* Assignment Details */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <h4 className="text-md font-medium text-gray-900 mb-3">Assignment Details</h4>
                {isLoadingAssignments ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    <span className="ml-2 text-gray-600">Loading assignments...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Trial Assignments */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-800 mb-2">Trial Assignments</h5>
                      {trialAssignments.length > 0 ? (
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="text-sm">
                            <span className="text-gray-600">Officers:</span>
                            <span className="font-medium ml-2">
                              {trialAssignments.map((assignment, index) => 
                                `${assignment.officer_name || assignment.officer_id || '-'}${assignment.assignment_date ? ` (${assignment.assignment_date})` : ''}`
                              ).join(', ')}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center text-gray-500 text-sm">
                          No trial assignments
                        </div>
                      )}
                    </div>

                    {/* Centre Assignments */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-800 mb-2">Centre Assignments</h5>
                      {centreAssignments.length > 0 ? (
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="text-sm">
                            <span className="text-gray-600">Officers:</span>
                            <span className="font-medium ml-2">
                              {centreAssignments.map((assignment, index) => 
                                `${assignment.officer_name || assignment.officer_id || '-'}${assignment.assignment_date ? ` (${assignment.assignment_date})` : ''}`
                              ).join(', ')}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center text-gray-500 text-sm">
                          No centre assignments
                        </div>
                      )}
                    </div>

                    {/* Location Assignments */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-800 mb-2">Location Assignments</h5>
                      {locationAssignments.length > 0 ? (
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="text-sm">
                            <span className="text-gray-600">Officers:</span>
                            <span className="font-medium ml-2">
                              {locationAssignments.map((assignment, index) => 
                                `${assignment.officer_name || assignment.officer_id || '-'}${assignment.assignment_date ? ` (${assignment.assignment_date})` : ''}`
                              ).join(', ')}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center text-gray-500 text-sm">
                          No location assignments
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Inline Data Entry Form */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">Enter Trial Data</h3>
                  <p className="text-gray-600 mt-2">
                    {loadedTrialData?.trialName || '-'} - {loadedLocationData?.locationName || '-'}
                  </p>
                  <div className="mt-2 text-sm text-gray-500">
                    <span className="mr-4">Trial Code: {loadedTrialData?.trialCode || '-'}</span>
                    <span className="mr-4">Type: {loadedTrialData?.trialType || '-'}</span>
                    <span>Year: {loadedTrialData?.year || '-'}</span>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Editing Status */}
                  {editingEntryId && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center">
                        <Edit className="w-5 h-5 text-blue-600 mr-2" />
                        <span className="text-blue-800 font-medium">Editing Mode</span>
                        <span className="text-blue-600 ml-2">- Update the fields below and click "Update Entry"</span>
                      </div>
                    </div>
                  )}

                  {/* Form Fields - Side by Side Layout */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Entry Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Entry Date</label>
                      <input
                        type="date"
                        className="input-field"
                        defaultValue={new Date().toISOString().split('T')[0]}
                        readOnly
                      />
                    </div>

                    {/* Select Observation */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Observation <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={currentEntry.observation}
                        onChange={(e) => handleCurrentEntryChange('observation', e.target.value)}
                        className="input-field"
                      >
                        <option value="">Select Observation</option>
                        {loadedTrialData?.observations?.map((obs, index) => (
                          <option key={index} value={`${obs.observation} (${obs.days} days)`}>
                            {obs.observation} ({obs.days} days)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Select Replication */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Replication <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={currentEntry.replication}
                        onChange={(e) => handleCurrentEntryChange('replication', e.target.value)}
                        className="input-field"
                        disabled={!currentEntry.observation}
                      >
                        <option value="">Select Replication</option>
                        {loadedTrialData?.replications && !isNaN(loadedTrialData.replications) ? (
                          Array.from({ length: parseInt(loadedTrialData.replications) }, (_, i) => (
                            <option key={i + 1} value={`R${i + 1}`}>
                              R{i + 1}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="R1">R1</option>
                            <option value="R2">R2</option>
                            <option value="R3">R3</option>
                            <option value="R4">R4</option>
                            <option value="R5">R5</option>
                            <option value="R6">R6</option>
                            <option value="R7">R7</option>
                            <option value="R8">R8</option>
                            <option value="R9">R9</option>
                            <option value="R10">R10</option>
                          </>
                        )}
                      </select>
                      {!currentEntry.observation && (
                        <p className="mt-1 text-xs text-gray-500">Please select an observation first</p>
                      )}
                    </div>

                    {/* Select Culture */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Culture <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={currentEntry.culture}
                        onChange={(e) => handleCurrentEntryChange('culture', e.target.value)}
                        className="input-field"
                        disabled={!currentEntry.replication}
                      >
                        <option value="">Select Culture</option>
                        {loadedTrialData?.trials?.map((trial, index) => (
                          <option key={index} value={trial.culture}>
                            {trial.culture}
                          </option>
                        )) || (
                          <>
                            <option value="V1">V1</option>
                            <option value="V2">V2</option>
                            <option value="V3">V3</option>
                            <option value="V4">V4</option>
                            <option value="V5">V5</option>
                          </>
                        )}
                      </select>
                      {!currentEntry.replication && (
                        <p className="mt-1 text-xs text-gray-500">Please select a replication first</p>
                      )}
                    </div>

                    {/* Enter Value */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Enter Value <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={currentEntry.value}
                        onChange={(e) => handleCurrentEntryChange('value', e.target.value)}
                        className="input-field"
                        placeholder="Enter the measured value"
                        disabled={!currentEntry.culture}
                      />
                      {!currentEntry.culture && (
                        <p className="mt-1 text-xs text-gray-500">Please select a culture first</p>
                      )}
                    </div>
                  </div>

                  {/* Photo Upload Section */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Field Photos & Documentation</h4>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        id="photo-upload"
                      />
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        <Camera className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-lg font-medium text-gray-900 mb-2">Click to upload photos or drag and drop</p>
                        <p className="text-sm text-gray-500">Supports JPG, PNG, GIF (Max 5MB each)</p>
                      </label>
                    </div>

                    {/* Photo Previews */}
                    {uploadedPhotos.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        {uploadedPhotos.map((photo) => (
                          <div key={photo.id} className="relative group">
                            <img
                              src={photo.preview}
                              alt={photo.name}
                              className="w-full h-24 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              onClick={() => removePhoto(photo.id)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            <p className="text-xs text-gray-500 mt-1 truncate">{photo.name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Feedback Sections */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Farmer Feedback & Observations
                      </label>
                      <textarea
                        rows="4"
                        value={farmerFeedback}
                        onChange={(e) => setFarmerFeedback(e.target.value)}
                        placeholder="Enter farmer's feedback, observations, concerns, or recommendations..."
                        className="input-field resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location Incharge Feedback
                      </label>
                      <textarea
                        rows="4"
                        value={locationInchargeFeedback}
                        onChange={(e) => setLocationInchargeFeedback(e.target.value)}
                        placeholder="Enter location incharge's technical observations, issues encountered, or recommendations..."
                        className="input-field resize-none"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={handleSaveEntry}
                      disabled={!currentEntry.observation || !currentEntry.replication || !currentEntry.culture || !currentEntry.value}
                      className="btn-primary px-6 whitespace-nowrap"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {editingEntryId ? 'Update Entry' : 'Save Entry'}
                    </button>
                    {editingEntryId && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="btn-outline px-4 whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    )}
                  </div>

                  {/* Saved Entries Table */}
                  {savedEntries.length > 0 && (
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Saved Entries ({savedEntries.length})</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Observation
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Replication
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Culture
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Value
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Entry Date
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Officer
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {savedEntries.map((entry) => (
                              <tr key={entry.id}>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {entry.observation}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {entry.replication}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {entry.culture}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {entry.value}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {entry.entryDate}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  <div>
                                    <div className="font-medium">{entry.officerName || '-'}</div>
                                    <div className="text-xs text-gray-500">{entry.officerEmail || '-'}</div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(entry.status || 'active')}`}>
                                    {entry.status || 'active'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex gap-2 justify-end">
                                    <button
                                      onClick={() => handleEditEntry(entry)}
                                      className="text-blue-600 hover:text-blue-800"
                                      title="Edit Entry"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteEntry(entry.id)}
                                      className="text-red-600 hover:text-red-800"
                                      title="Delete Entry"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                </div>
              </div>
              </>
            )}
          </div>
        )}

        {/* No Selection Message */}
        {!showLocationDetails && (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select Trial Details</h3>
            <p className="text-gray-600">Please select Trial, Centre, and Location above and click Apply to view details.</p>
          </div>
        )}

        {/* Data Entry Modal */}
        {showDataEntryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Enter Trial Data</h2>
                  <button
                    onClick={() => setShowDataEntryModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <p className="text-gray-600 mt-2">
                  {selectedTrial?.trialName} - {selectedLocation?.locationName}
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Editing Status */}
                {editingEntryId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center">
                      <Edit className="w-5 h-5 text-blue-600 mr-2" />
                      <span className="text-blue-800 font-medium">Editing Mode</span>
                      <span className="text-blue-600 ml-2">- Update the fields below and click "Update Entry"</span>
                    </div>
                  </div>
                )}

                {/* Entry Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Entry Date</label>
                  <input
                    type="date"
                    className="input-field"
                    defaultValue={new Date().toISOString().split('T')[0]}
                    readOnly
                  />
                </div>

                {/* Select Observation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Observation <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={currentEntry.observation}
                    onChange={(e) => handleCurrentEntryChange('observation', e.target.value)}
                    className="input-field"
                  >
                    <option value="">Select Observation</option>
                    <option value="Plant Height">Plant Height (cm)</option>
                    <option value="Number of Plants">Number of Plants</option>
                    <option value="Number of Tillers">Number of Tillers</option>
                    <option value="Panicle Length">Panicle Length (cm)</option>
                    <option value="Days to Flowering">Days to Flowering</option>
                    <option value="Days to Maturity">Days to Maturity</option>
                    <option value="Yield">Yield (kg/ha)</option>
                    <option value="1000 Grain Weight">1000 Grain Weight (g)</option>
                    <option value="Disease Incidence">Disease Incidence (%)</option>
                    <option value="Pest Incidence">Pest Incidence (%)</option>
                  </select>
                </div>

                {/* Select Replication */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Replication <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={currentEntry.replication}
                    onChange={(e) => handleCurrentEntryChange('replication', e.target.value)}
                    className="input-field"
                    disabled={!currentEntry.observation}
                  >
                    <option value="">Select Replication</option>
                    <option value="R1">R1</option>
                    <option value="R2">R2</option>
                    <option value="R3">R3</option>
                    <option value="R4">R4</option>
                    <option value="R5">R5</option>
                    <option value="R6">R6</option>
                    <option value="R7">R7</option>
                    <option value="R8">R8</option>
                    <option value="R9">R9</option>
                    <option value="R10">R10</option>
                  </select>
                  {!currentEntry.observation && (
                    <p className="mt-1 text-xs text-gray-500">Please select an observation first</p>
                  )}
                </div>

                {/* Select Culture */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Culture <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={currentEntry.culture}
                    onChange={(e) => handleCurrentEntryChange('culture', e.target.value)}
                    className="input-field"
                    disabled={!currentEntry.replication}
                  >
                    <option value="">Select Culture</option>
                    <option value="V1">V1</option>
                    <option value="V2">V2</option>
                    <option value="V3">V3</option>
                    <option value="V4">V4</option>
                    <option value="V5">V5</option>
                  </select>
                  {!currentEntry.replication && (
                    <p className="mt-1 text-xs text-gray-500">Please select a replication first</p>
                  )}
                </div>

                {/* Enter Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Value <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      step="0.01"
                      value={currentEntry.value}
                      onChange={(e) => handleCurrentEntryChange('value', e.target.value)}
                      className="input-field flex-1"
                      placeholder="Enter the measured value"
                      disabled={!currentEntry.culture}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSaveEntry}
                        disabled={!currentEntry.observation || !currentEntry.replication || !currentEntry.culture || !currentEntry.value}
                        className="btn-primary px-6 whitespace-nowrap"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {editingEntryId ? 'Update Entry' : 'Save Entry'}
                      </button>
                      {editingEntryId && (
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="btn-outline px-4 whitespace-nowrap"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                  {!currentEntry.culture && (
                    <p className="mt-1 text-xs text-gray-500">Please select a culture first</p>
                  )}
                </div>

                {/* Saved Entries Table */}
                {savedEntries.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Saved Entries ({savedEntries.length})
                    </label>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Observation
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Replication
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Culture
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Value
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Entry Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Officer
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {savedEntries.map((entry) => (
                            <tr key={entry.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {entry.observation}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                  {entry.replication}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {entry.culture}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                {entry.value}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {entry.entryDate}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">{entry.officerName || '-'}</div>
                                  <div className="text-xs text-gray-500">{entry.officerEmail || '-'}</div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(entry.status || 'active')}`}>
                                  {entry.status || 'active'}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex gap-2 justify-end">
                                  <button
                                    onClick={() => handleEditEntry(entry)}
                                    className="text-blue-600 hover:text-blue-800"
                                    title="Edit Entry"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteEntry(entry.id)}
                                    className="text-red-600 hover:text-red-800"
                                    title="Delete Entry"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Photo Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Field Photos & Documentation</label>
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                      <input 
                        type="file" 
                        multiple 
                        accept="image/*" 
                        onChange={handlePhotoUpload} 
                        className="hidden" 
                        id="photo-upload" 
                      />
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-1">Click to upload photos or drag and drop</p>
                        <p className="text-xs text-gray-500">Supports JPG, PNG, GIF (Max 5MB each)</p>
                      </label>
                    </div>
                    
                    {uploadedPhotos.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Uploaded Photos ({uploadedPhotos.length})</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {uploadedPhotos.map((photo) => (
                            <div key={photo.id} className="relative group">
                              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                                <img 
                                  src={photo.preview} 
                                  alt={photo.name} 
                                  className="w-full h-full object-cover cursor-pointer" 
                                  onClick={() => setPhotoPreview(photo)}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                                  <button 
                                    onClick={() => removePhoto(photo.id)} 
                                    className="opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-all duration-200"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 mt-1 truncate">{photo.name}</p>
                              <p className="text-xs text-gray-500">{(photo.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Feedback Section */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Farmer Feedback & Observations
                    </label>
                    <textarea
                      rows="4"
                      value={farmerFeedback}
                      onChange={(e) => setFarmerFeedback(e.target.value)}
                      placeholder="Enter farmer's feedback, observations, concerns, or recommendations..."
                      className="input-field resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location Incharge Feedback
                    </label>
                    <textarea
                      rows="4"
                      value={locationInchargeFeedback}
                      onChange={(e) => setLocationInchargeFeedback(e.target.value)}
                      placeholder="Enter location incharge's technical observations, issues encountered, or recommendations..."
                      className="input-field resize-none"
                    />
                  </div>
                </div>



                {/* Final Submit Section */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDataEntryModal(false);
                      setSavedEntries([]);
                      setCurrentEntry({
                        observation: '',
                        replication: '',
                        culture: '',
                        value: ''
                      });
                      setFarmerFeedback('');
                      setLocationInchargeFeedback('');
                      setEditingEntryId(null);
                    }}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (savedEntries.length === 0) {
                        toast.error('Please add at least one entry before submitting');
                        return;
                      }
                      
                      // Save all entries to localStorage
                      const existingAssignments = JSON.parse(localStorage.getItem('trialAssignments') || '[]');
                      const assignmentIndex = existingAssignments.findIndex(a => a.id === selectedLocation.id);
                      
                      if (assignmentIndex !== -1) {
                        existingAssignments[assignmentIndex] = {
                          ...existingAssignments[assignmentIndex],
                          lastEntryDate: new Date().toISOString().split('T')[0],
                          entriesByLocation: {
                            ...existingAssignments[assignmentIndex].entriesByLocation,
                            [new Date().toISOString().split('T')[0]]: savedEntries
                          }
                        };
                        
                        localStorage.setItem('trialAssignments', JSON.stringify(existingAssignments));
                        toast.success(`${savedEntries.length} entries saved successfully!`);
                        setShowDataEntryModal(false);
                        setSavedEntries([]);
                        setCurrentEntry({
                          observation: '',
                          replication: '',
                          culture: '',
                          value: ''
                        });
                        setFarmerFeedback('');
                        setLocationInchargeFeedback('');
                        setEditingEntryId(null);
                      }
                    }}
                    disabled={savedEntries.length === 0}
                    className="btn-primary"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Submit All Entries ({savedEntries.length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Photo Preview Modal */}
        {photoPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl max-h-[90vh] overflow-hidden">
              <button
                onClick={() => setPhotoPreview(null)}
                className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75 transition-all"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={photoPreview.preview}
                alt={photoPreview.name}
                className="max-w-full max-h-full object-contain"
              />
              <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded-lg">
                <p className="text-sm font-medium">{photoPreview.name}</p>
                <p className="text-xs">
                  {(photoPreview.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Location Details Modal */}
        {showLocationDetailsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Location Details</h2>
                  <button
                    onClick={() => setShowLocationDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <p className="text-gray-600 mt-2">
                  {selectedLocation?.locationName}
                </p>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location Code
                    </label>
                    <p className="text-gray-900">{selectedLocation?.locationCode}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Officer
                    </label>
                    <p className="text-gray-900">{selectedLocation?.assignedOfficer}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Culture
                    </label>
                    <p className="text-gray-900">{selectedLocation?.culture || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Treatment
                    </label>
                    <p className="text-gray-900">{selectedLocation?.treatment || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Replications
                    </label>
                    <p className="text-gray-900">{selectedLocation?.replications || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedLocation?.status)}`}>
                      {selectedLocation?.status}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Observations
                  </label>
                  <div className="bg-gray-50 rounded-lg p-3">
                    {selectedLocation?.observations && selectedLocation.observations.length > 0 ? (
                      <ul className="space-y-1">
                        {selectedLocation.observations.map((obs, index) => (
                          <li key={index} className="text-sm text-gray-700">• {obs}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500 text-sm">No observations recorded</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowLocationDetailsModal(false)}
                    className="btn-outline"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrialDataEntry; 